var $class="se2--content-scroll-highlights",$name="ContentScrollHighlights",$path="app/components/ContentScrollHighlights/index.js",$this={$class,$name,$path,};import lazyHandleOpenModalButtons from 'app/components/utilities/lazyHandleOpenModalButtons';

import 'app/modules/accessibility';
import shell from 'app/modules/shell';

import 'app/partials/image';

import isMobileBreakpoint from 'app/utilities/isMobileBreakpoint';
import isUsingKeyboard from 'app/utilities/isUsingKeyboard';
import setCSSVariable from 'app/utilities/setCSSVariable';

import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import debounce from 'lodash/debounce';
import forEach from 'lodash/forEach';

import {
  sliderContainerClassName,
  slideElementClassName,
} from './config';

const sliderElementClassName = 'slider';
const bannerContentElementClassName = 'banner-content';
const cardColumnContainerClassName = 'card-column-container';

const carouselPaddingBlockStart = 40;
const descriptionMarginBlock = 130;
const mobileCellWidth = 196;
const mobileCellMargin = 16;
const scrollSpeed = 30;

export default shell.registerComponent($this, ({
  addEventListener, configs, mount, settings, subscribeToMessage, unmount,
}) => {
  let slider = null;

  const initSlider = (element) => {
    const bannerContentElement = element.querySelector(`.${bannerContentElementClassName}`);
    const sliderContainer = element.querySelector(`.${sliderContainerClassName}`);
    const sliderElement = element.querySelector(`.${sliderElementClassName}`);
    const slideElements = element.querySelectorAll(`.${slideElementClassName}`);

    let endScrollPoint = sliderElement.getBoundingClientRect().height
      - sliderContainer.clientHeight + carouselPaddingBlockStart;
    let isKeyboard = false;
    let isPageScrolling = false;
    let isSliderHovered = false;
    let isSliderLoaded = false;
    let isSliderVisible = false;

    const timeline = gsap.timeline({
      yoyo: true,
      repeat: -1,
    });

    const createMobileCardContainer = () => {
      const div = document.createElement('div');
      div.classList.add(cardColumnContainerClassName);
      return div;
    };

    const reinitializeLayout = (breakpoint) => {
      sliderElement.innerHTML = '';

      if (breakpoint === configs.breakpoints.xs.key) {
        let div = createMobileCardContainer();

        forEach(
          slideElements,
          (slide, i) => {
            if (i % 2 === 0 && i !== 0) {
              div = createMobileCardContainer();
              if (slideElements.length - 1 === i) sliderElement.append(div);
            } else {
              sliderElement.append(div);
            }
            div.append(slide);
          },
        );

        // non-logical style is used in order to correctly perform with gsap
        sliderElement.style.width = `${Math.ceil(slideElements.length / 2) * mobileCellWidth + mobileCellMargin}px`;
        sliderElement.style.transform = `translate3d(${
          settings.direction === configs.directions.rtl ? '' : '-'
        }16px, 0px, 0px)`;
      } else {
        forEach(
          slideElements,
          (slide) => {
            sliderElement.append(slide);
          },
        );

        sliderElement.style.width = 'auto';
        sliderElement.style.transform = 'initial';
      }
    };

    const clearTimeline = () => {
      sliderContainer.scrollTop = 0;
      timeline.progress(0);
      timeline.clear();
      timeline.pause();
    };

    const initAnimation = (isFirstLoad = false) => {
      const startAnimation = () => {
        clearTimeline();
        endScrollPoint = sliderElement.getBoundingClientRect().height
          - sliderContainer.clientHeight + carouselPaddingBlockStart;

        timeline.to(
          sliderContainer,
          {
            duration: Math.round(endScrollPoint / scrollSpeed),
            ease: 'none',
            scrollTo: {
              y: endScrollPoint,
            },
            overwrite: 'all',
          },
        );

        if (!settings.accessibility && !isKeyboard && !isPageScrolling && isSliderVisible) {
          timeline.play();
        } else if (isFirstLoad) {
          // need to initialize timeline for correct updates on interaction
          timeline.play();
          timeline.pause();
        }
      };

      const startAnimationOnImagesLoad = () => {
        isSliderLoaded = false;
        let allImagesCounter = 0;
        let loadedImagesCounter = 0;

        const countLoadedImages = () => {
          loadedImagesCounter += 1;

          if (loadedImagesCounter === allImagesCounter) {
            isSliderLoaded = true;
            startAnimation();
          }
        };

        forEach(
          slideElements,
          (slide) => {
            const imageElement = slide.querySelector('img');

            if (imageElement && !imageElement.complete) {
              allImagesCounter += 1;

              addEventListener(imageElement, 'load', countLoadedImages);
              addEventListener(imageElement, 'error', countLoadedImages);
            }
          },
        );

        if (!allImagesCounter) {
          isSliderLoaded = true;
          startAnimation();
        }
      };

      clearTimeline();

      setCSSVariable(
        configs.css.variableNames.logosHighlightHeight,
        `${bannerContentElement.scrollHeight + descriptionMarginBlock * 2}px`,
        element,
      );

      if (!isMobileBreakpoint()) {
        setTimeout(startAnimationOnImagesLoad, 1000);
      }
    };

    const updateSliderProgress = () => {
      if (!isMobileBreakpoint() && !settings.accessibility) {
        timeline.progress(sliderContainer.scrollTop / endScrollPoint);
      }
    };

    const onMouseEnter = () => {
      isSliderHovered = true;
      timeline.pause();
    };

    const onMouseLeave = () => {
      isSliderHovered = false;
      updateSliderProgress();

      if (
        !settings.accessibility
        && !isKeyboard
        && !isMobileBreakpoint()
        && isSliderVisible
      ) timeline.play();
    };

    const onKeyDown = (event) => {
      if (isUsingKeyboard()) {
        const { code, target } = event;

        if (code === configs.keyNames.enter) {
          if (target.classList.contains('slide')) {
            target.querySelector('.button').click();
          }
        }
      }
    };

    const onKeyboardPress = ({ isKeyboardUsed }) => {
      if (isKeyboardUsed && isKeyboard !== isKeyboardUsed) {
        isKeyboard = isKeyboardUsed;

        timeline.progress(0);
        timeline.clear();
        timeline.pause();
      } else if (!isKeyboardUsed && isKeyboard !== isKeyboardUsed) {
        isKeyboard = isKeyboardUsed;

        if (!settings.accessibility && !isMobileBreakpoint()) {
          initAnimation();
        }
      }
    };

    const onCardFocus = (e) => {
      if (isMobileBreakpoint()) {
        gsap.to(window, { duration: 0.3, scrollTo: { y: sliderContainer }, ease: 'power4.out' });
        gsap.to(sliderContainer, { duration: 0.3, scrollTo: { x: e.target }, ease: 'power4.out' });
      }
    };

    const onAccessibilityToggle = () => {
      if (settings.accessibility) {
        clearTimeline();
      } else {
        initAnimation();
      }
    };

    const pauseTimelineOnScroll = debounce(() => {
      isPageScrolling = true;
      timeline.pause();
    }, 400, { leading: true, trailing: false });

    const startTimelineOnScroll = debounce(() => {
      isPageScrolling = false;

      if (
        isSliderVisible
        && isSliderLoaded
        && !isKeyboard
        && !isSliderHovered
        && !isMobileBreakpoint()
        && !settings.accessibility
      ) timeline.play();
    }, 450);

    addEventListener(window, 'scroll', pauseTimelineOnScroll, { passive: true });
    addEventListener(window, 'scroll', startTimelineOnScroll, { passive: true });

    addEventListener(sliderContainer, 'keydown', onKeyDown);

    forEach(
      slideElements,
      (slide) => {
        addEventListener(slide, 'focus', onCardFocus);
      },
    );

    subscribeToMessage(configs.messages.breakpoint, reinitializeLayout);
    subscribeToMessage(configs.messages.isKeyboardUsed, onKeyboardPress);
    subscribeToMessage(configs.messages.accessibility, onAccessibilityToggle);

    if (isMobileBreakpoint()) {
      reinitializeLayout(configs.breakpoints.xs.key);
    }

    initAnimation(true);

    const intersectionObserver = new IntersectionObserver(([{ intersectionRatio }]) => {
      isSliderVisible = intersectionRatio > 0;
      (!isMobileBreakpoint() && !isPageScrolling && isSliderVisible) ? timeline.play() : timeline.pause();
    });

    intersectionObserver.observe(element);

    const commonOptions = {
      activeCursor: 'grabbing',
      allowContextMenu: true,
      bounds: sliderContainer,
      cursor: 'grab',
      dragClickables: true,
      throwProps: true,
      zIndexBoost: false,
    };

    return {
      draggableMobile: new Draggable(
        sliderElement,
        {
          ...commonOptions,
          type: 'x',
        },
      ),
      draggableDesktop: new Draggable(
        sliderContainer,
        {
          ...commonOptions,
          type: 'scrollTop',
          onPress: () => timeline.pause(),
          onDragEnd: updateSliderProgress,
        },
      ),
      element,
      initAnimation,
      intersectionObserver,
      onMouseEnter,
      onMouseLeave,
      sliderContainer,
    };
  };

  const reinitDraggables = () => {
    if (isMobileBreakpoint()) {
      slider.draggableDesktop.disable();
      slider.draggableMobile.enable();
    } else {
      slider.draggableMobile.disable();
      slider.draggableDesktop.enable();
    }
    slider.initAnimation();
  };

  addEventListener(window, 'resize', reinitDraggables);

  mount((element) => {
    slider = initSlider(element);

    if (slider) {
      addEventListener(slider.sliderContainer, 'mouseenter', slider.onMouseEnter);
      addEventListener(slider.sliderContainer, 'mouseleave', slider.onMouseLeave);
      reinitDraggables();
      lazyHandleOpenModalButtons(addEventListener, element);
    }
  });

  unmount(() => {
    slider.intersectionObserver.disconnect();
  });
});
